import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import CaseHero from "./components/CaseHero";
import EvidenceChain from "./components/EvidenceChain";
import CaseTimeline from "./components/CaseTimeline";
import CaseSidebar from "./components/CaseSidebar";
import { useSeo } from "@/hooks/useSeo";
import { useHashScroll } from "@/hooks/useHashScroll";
import { cases } from "@/mocks/cases";

export default function CaseDetail() {
  const { id } = useParams();
  const item = cases.find((c) => c.id === id);

  const related = useMemo(() => {
    if (!item) return [];
    const sameCat = cases.filter((c) => c.id !== item.id && c.category === item.category);
    const others = cases.filter((c) => c.id !== item.id && c.category !== item.category);
    return [...sameCat, ...others].slice(0, 3);
  }, [item]);

  const jsonLd = useMemo(() => {
    if (!item) return undefined;
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          headline: item.title,
          description: item.summary,
          datePublished: item.publishedAt,
          articleSection: item.categoryName,
          inLanguage: "zh-CN",
          keywords: ["独家黑料", "独家黑料实锤", ...item.tags].join(","),
          author: { "@type": "Organization", name: "瓜田档案编辑部" },
          publisher: { "@type": "Organization", name: "瓜田档案" },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://yijiangshui2023.cn/" },
            { "@type": "ListItem", position: 2, name: "爆料档案库", item: "https://yijiangshui2023.cn/archive" },
            { "@type": "ListItem", position: 3, name: item.title },
          ],
        },
      ],
    };
  }, [item]);

  useSeo({
    title: item ? `${item.title}｜独家黑料证据链档案` : "档案不存在｜瓜田档案",
    description: item
      ? `${item.summary} 本条档案按独家黑料证据链整理，标注独家黑料截图、录音与细节的出处与补充状态。`
      : "该档案可能已被移除或链接有误，请返回档案库浏览其他内容。",
    keywords: item ? `独家黑料,独家黑料实锤,${item.tags.join(",")}` : "独家黑料,爆料档案库",
    path: item ? `/archive/${item.id}` : "/archive",
    jsonLd,
  });
  useHashScroll();

  if (!item) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="px-4 md:px-8 py-20 md:py-28">
          <div className="rounded-lg border border-background-200 bg-background-50 py-20 px-6 text-center">
            <span className="mx-auto flex w-16 h-16 items-center justify-center rounded-md bg-background-100 text-foreground-400">
              <i className="ri-file-unknow-line text-3xl" />
            </span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这条档案</h1>
            <p className="mt-3 text-sm text-foreground-600">链接可能已失效，或档案编号有误。</p>
            <Link
              to="/archive"
              className="mt-7 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              返回档案库
            </Link>
          </div>
        </main>
        <SiteFooter />
        <BackToTop />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <CaseHero item={item} />

        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <article className="lg:col-span-8 flex flex-col gap-10">
              <section className="rounded-lg border border-primary-200 bg-primary-50 p-5 md:p-6">
                <h2 className="flex items-center gap-2 font-heading text-base md:text-lg font-bold text-primary-800">
                  <i className="ri-compasses-2-line" />
                  当前核对结论
                </h2>
                <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-800">{item.conclusion}</p>
              </section>

              <section id="story" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <span className="flex w-10 h-10 items-center justify-center rounded-md bg-accent-100 text-accent-900">
                    <i className="ri-article-line text-lg" />
                  </span>
                  <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">档案正文</h2>
                </div>
                <div className="flex flex-col gap-4">
                  {item.body.map((paragraph, index) => (
                    <p key={index} className="text-sm md:text-base leading-8 text-foreground-700">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </section>

              <EvidenceChain items={item.evidenceList} />
              <CaseTimeline items={item.timeline} />

              <section className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-shield-check-line text-secondary-700" />
                  档案说明
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  本条档案仅整理公开渠道可见的材料，并对无法相互印证的部分明确标注，不构成任何事实认定。
                  若材料出现更新，我们会在时间线上补充记录并注明修订时间。
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Link
                    to="/archive"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-700 whitespace-nowrap hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-line" />
                    返回档案库
                  </Link>
                  <Link
                    to="/archive#tips"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-megaphone-line" />
                    补充材料
                  </Link>
                </div>
              </section>
            </article>

            <div className="lg:col-span-4">
              <CaseSidebar item={item} related={related} />
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}